import { useEffect, useState } from "react";
import classNames from "classnames";
import useConfig from "@lib/use_config";
import useContent from "@lib/use_content";
import styles from "@hudson/ui/styles/Form.module.css";

interface LayoutProps {
  children: JSX.Element;
  maxWidthClass?: string;
  onPageChange: (page: be.ProfilePage) => void;
  initialTab?: be.ProfilePage;
}

export default function Layout({
  children,
  maxWidthClass = "max-w-4xl",
  onPageChange,
  initialTab = "profile",
}: LayoutProps) {
  const { loading: cfgLoading, data: cfgData } = useConfig([
    "disableProfileCreditCards",
    "disableProfileLocations",
    "enableProfileLinkedGroups",
    "showProfileFrequentRiderPoints",
  ]);
  const { data, loading } = useContent([
    "beProfileContactTab",
    "beProfileLocationsTab",
    "beProfileLinkedGroupsTab",
    "beProfilePasswordTab",
    "beProfilePaymentTab",
    "beProfilePointsTab",
    "beProfileReservationsTab",
    "beProfileHomeTitle",
  ]);
  const [currentTab, setCurrentTab] = useState<be.ProfilePage>(initialTab);

  interface Tab {
    name: string;
    value: be.ProfilePage;
  }

  const tabs: Tab[] = [
    { name: data?.content.beProfileContactTab || "", value: "profile" },
    { name: data?.content.beProfilePasswordTab || "", value: "password" },
  ];

  if (!cfgData?.config.disableProfileLocations) {
    tabs.push({ name: data?.content.beProfileLocationsTab || "", value: "locations" });
  }

  if (!cfgData?.config.disableProfileCreditCards) {
    tabs.push({ name: data?.content.beProfilePaymentTab || "", value: "payment" });
  }

  tabs.push({ name: data?.content.beProfileReservationsTab || "", value: "reservations" });

  if (cfgData?.config.showProfileFrequentRiderPoints) {
    tabs.push({ name: data?.content.beProfilePointsTab || "", value: "points" });
  }

  if (cfgData?.config.enableProfileLinkedGroups) {
    tabs.push({ name: data?.content.beProfileLinkedGroupsTab || "", value: "linkedGroup" });
  }

  useEffect(() => {
    onPageChange(currentTab);
  }, []);

  useEffect(() => {
    setCurrentTab(initialTab);
  }, [initialTab]);

  function onTabChange(newTab: be.ProfilePage) {
    onPageChange(newTab);
  }

  return (
    <>
      <div className="relative">
        <div>
          <div className={`${maxWidthClass} mx-auto flex flex-col xs:px-0 xl:px-0`}>
            <main className="flex-1">
              <div className={`relative ${maxWidthClass} mx-auto xs:px-0 xl:px-0`}>
                <div className="pt-10">
                  <div className="px-4 sm:px-6 md:px-0">
                    <h1 className="mb-0 text-3xl md:text-3xl font-extrabold flex items-center flex-wrap opacity-70">
                      {data?.content.beProfileHomeTitle}
                    </h1>
                  </div>
                  <div className="px-4 sm:px-6 md:px-0">
                    <div className="py-5">
                      {/* Tabs */}
                      <div className="md:hidden">
                        <select
                          id="selected-tab"
                          name="selected-tab"
                          onChange={(e) => {
                            setCurrentTab(e.target.value as be.ProfilePage);
                            onTabChange(e.target.value as be.ProfilePage);
                          }}
                          className={classNames(
                            styles.focusBorder,
                            "mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-400 sm:text-sm rounded-md",
                          )}
                          value={currentTab}
                        >
                          {tabs.map((tab) => (
                            <option value={tab.value} key={tab.value}>
                              {tab.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="hidden md:block">
                        <div className="border-b border-gray-200">
                          <nav className="-mb-px flex space-x-8">
                            {tabs.map((tab) => (
                              <a
                                key={tab.value}
                                onClick={(e) => {
                                  setCurrentTab(tab.value);
                                  onTabChange(tab.value);
                                }}
                                className={classNames(
                                  tab.value == currentTab
                                    ? `${styles.link} `
                                    : `${styles.link} text-gray-700 border-transparent hover:border-gray-400 `,
                                  "whitespace-nowrap py-2 px-1 border-b-4 font-medium text-base cursor-pointer",
                                )}
                                style={{
                                  borderColor:
                                    tab.value == currentTab
                                      ? "var(--primary-color)"
                                      : "transparent",
                                }}
                              >
                                {tab.name}
                              </a>
                            ))}
                          </nav>
                        </div>
                      </div>

                      {children}
                    </div>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </>
  );
}
